/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {fetch, FetchScope} from '@adobe/exc-app/network';
import {getLocalCache} from '@exc/storage';
import metrics, {Level} from '@adobe/exc-app/metrics';
import {staleWhileRevalidate} from '../utils';
import {WorkHubItem, WorkHubOptions, WorkHubResponse} from '../models/WorkHub';

const STORAGE_KEY = 'unifiedShellWorkHub';
const EXPIRY = 24 * 60 * 60 * 1000;
const localCache = getLocalCache<WorkHubResponse>(STORAGE_KEY);

export default class WorkHubService {
  private readonly promises: Record<string, Promise<WorkHubResponse|undefined>> = {};
  private readonly metrics = metrics.create('exc.core.services.WorkHubService');
  private readonly fetched = new Set;

  /**
   * Returns an existing response for a Work Hub request.
   * Looks at the cache first, and then at pending requests.
   * @param {string} key - unique key (imsOrgId:locale:product)
   * @returns {Promise<WorkHubResponse|undefined>} existing response if available.
   * @private
   */
  private async getExistingResponse(key: string): Promise<WorkHubResponse|undefined> {
    try {
      // Look for existing result (or pending result) in memory
      const existingPromise = this.promises[key];

      // Look for cached result. Note localCache already verifies expiry.
      let response = await localCache.get(key);

      // If no cached, use a pending response if we have one.
      if (existingPromise && !response) {
        response = await existingPromise;
      }

      if (response && Date.now() < response.expires) {
        return response;
      }
    } catch (err) {
      this.metrics.warn('Failed to get Work Hub response from cache or memory', {err});
    }
  }

  /**
   * Gets Work Hub response from cache or fetch
   * @param options - parameters to send to Work Hub
   * @returns {Promise<WorkHubResponse|undefined>} the constructed Work Hub response
   */
  async getWorkHub(options: WorkHubOptions): Promise<WorkHubResponse|undefined> {
    const {imsOrgId, ioGateway, locale, product, sandbox} = options;

    // Prevent storing data with null id
    if (!imsOrgId) {
      return;
    }

    const key = `${imsOrgId}:${sandbox}:${locale}:${product}`;
    const response = await this.getExistingResponse(key);

    // Don't need to go through the staleWhileRevalidate workflow every single
    // time. It's more of a once per page load kind of experience. If the
    // key has been fetched this session, return the cached response.
    if (this.fetched.has(key) && response) {
      return response;
    }

    // A request must be made to get the latest. Add the key to the fetched set
    // so subsequent requests do not make additional requests this load.
    this.fetched.add(key);

    const getWorkHubPromise = (): Promise<WorkHubResponse|undefined> => {
      const workHubPromise = this.fetchWorkhub({ioGateway, locale, product})
        .then(workHubResponse => this.onComplete(key, workHubResponse))
        .catch(error => {
          !!response && this.onError(key, error);
          return undefined;
        })
        .finally(() => delete this.promises[key]);
      this.promises[key] = workHubPromise;
      return workHubPromise;
    };

    // If we have no response, wait for this promise.
    // Otherwise, it used for a future request (stale-while-revalidate)
    return await staleWhileRevalidate<WorkHubResponse|undefined>(getWorkHubPromise, response);
  }

  /**
   * Fetches Work Hub response from service
   * @param {WorkHubOptions} options - parameters to send to Work Hub
   * @returns {Promise<WorkHubItem[]>} a promise with the Work Hub response
   */
  private async fetchWorkhub(options: WorkHubOptions): Promise<WorkHubItem[]> {
    const {ioGateway, locale, product} = options;
    const url = `${ioGateway}/navigation/v1/capabilities?product=${product}&locale=${locale}`;
    const res = await fetch(url, {
      auth: 'Header',
      headers: {
        'Content-Type': 'application/json',
        'x-sandbox-name': 'none' // this is the default - overidden in @adobe/exc-app/network
      },
      method: 'GET',
      scope: FetchScope.SANDBOX
    });

    let data: any;
    let success: boolean;

    try {
      data = await res.json();
      success = true;
    } catch (error) {
      data = {error: error.message};
      success = false;
    }

    if (!success) {
      throw data; // async converts to reject(data);
    }
    return data; // async converts to resolve(data);
  }

  /**
   * Caches a Work Hub response after its complete.
   * @param {string} key - Work Hub key (imsOrgId:locale:product)
   * @param {WorkHubItem[]} response - Work Hub items from response
   * @returns {WorkHubResponse} response - Response augmented with expiry data.
   * @private
   */
  private onComplete(key: string, response: WorkHubItem[]): WorkHubResponse {
    const fulfilled = Date.now();
    const expires = fulfilled + EXPIRY;
    const cacheResponse = {expires, fulfilled, items: response} as WorkHubResponse;
    this.metrics.event('Core.workHubFetchSuccess', {items: response.map(item => item.displayName)}, {level: Level.INFO});
    this.metrics.event('WorkHub.cacheSet', {expires, key});
    localCache.set(key, cacheResponse);
    return cacheResponse;
  }

  /**
   * Logs an error for background, non-blocking Work Hub failures.
   * @param {string} key
   * @param {Error} error - Error information
   * @private
   */
  private onError(key: string, error: Error & {url: string}): void {
    // The request failed for this key, so don't cache that response in the
    // fetched set. Now subsequent requests will re-fetch.
    this.fetched.delete(key);
    const message = error && error.message || 'Unknown error';
    const url = error.url;
    this.metrics.event('WorkHub.backgroundError', {cookie: document.cookie.length, key, message, url}, {level: Level.ERROR});
  }
}
